// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Modal, Platform, Pressable, ScrollView, StyleSheet, TextInput } from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { Ionicons } from '@expo/vector-icons';
import { Redirect, useLocalSearchParams, useRootNavigationState, useRouter } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';

import { Text, View } from '@/components/Themed';
import { useI18n } from '@/i18n';
import { getSharedBffWebSocket, GolfCourseChangedEvent, GolfCourseUpsertRejectedEvent } from '@/services/bffWebSocket';
import { useUser } from './user-context';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { useRoundState } from '@/stores/roundState';
import { useManageableCourses, type ManageableCourse } from '@/hooks/useManageableCourses';
import appColors from '@/theme/colors';

const HOLE_COUNT = 18;

const createRequestId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    const uuid = (crypto as { randomUUID?: () => string }).randomUUID?.();
    if (uuid) return uuid;
  }
  return `req-${Date.now()}`;
};

type HoleRow = { number: number; par: string; strokeIndex: string };
type TeeRow = {
  id: string;
  name: string;
  sex: string;
  courseRating: string;
  slopeRating: string;
};

type SaveState = {
  status: 'idle' | 'saving' | 'success' | 'error';
  message?: string | null;
};

type ScanState = {
  status: 'idle' | 'scanning' | 'error';
  message?: string | null;
};

type ScanResponseHole = { number: number; par: number; strokeIndex: number | null };
type ScanResponse = {
  holes?: ScanResponseHole[];
  totalPar?: number | null;
  source?: string;
  warnings?: string[];
};

type RoundSummary = {
  courseId?: string;
  CourseId?: string;
  courseName?: string;
  CourseName?: string;
  courseAddress?: string;
  CourseAddress?: string;
};

type GeoCourseDetail = {
  id?: string;
  Id?: string;
  name?: string;
  Name?: string;
  address?: string;
  Address?: string;
  visibility?: string;
  Visibility?: string;
  holes?: Array<{ number?: number; Number?: number; par?: number; Par?: number; strokeIndex?: number; StrokeIndex?: number }>;
  Holes?: Array<{ number?: number; Number?: number; par?: number; Par?: number; strokeIndex?: number; StrokeIndex?: number }>;
  tees?: Array<{
    id?: string;
    Id?: string;
    name?: string;
    Name?: string;
    sex?: string;
    Sex?: string;
    courseRating?: number;
    CourseRating?: number;
    slopeRating?: number;
    SlopeRating?: number;
    par?: number;
    Par?: number;
  }>;
  Tees?: Array<{
    id?: string;
    Id?: string;
    name?: string;
    Name?: string;
    sex?: string;
    Sex?: string;
    courseRating?: number;
    CourseRating?: number;
    slopeRating?: number;
    SlopeRating?: number;
    par?: number;
    Par?: number;
  }>;
};

const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const normalizeGuid = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!GUID_REGEX.test(trimmed)) return null;
  return trimmed.toLowerCase();
};

export default function ConfigCourseScreen() {
  const { roundId: roundIdParam, closeOnSave: closeOnSaveParam, manage: manageParam } =
    useLocalSearchParams<{ roundId?: string; closeOnSave?: string; manage?: string }>();
  const router = useRouter();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const isFocused = useIsFocused();
  const { t } = useI18n();
  const { user } = useUser();
  const storedRoundId = useRoundState((state) => state.roundId);
  const routeRoundId = useMemo(() => (Array.isArray(roundIdParam) ? roundIdParam[0] : roundIdParam) ?? null, [roundIdParam]);
  const closeOnSave = useMemo(() => {
    const value = (Array.isArray(closeOnSaveParam) ? closeOnSaveParam[0] : closeOnSaveParam) ?? '';
    return value === '1' || value === 'true';
  }, [closeOnSaveParam]);
  const effectiveRoundId = routeRoundId ?? storedRoundId;
  // Management mode (opened from the "Golf courses" menu): shows a selector of courses the user may
  // manage so they can pick one to edit, in addition to creating a new one.
  const manageMode = useMemo(() => {
    const value = (Array.isArray(manageParam) ? manageParam[0] : manageParam) ?? '';
    return value === '1' || value === 'true';
  }, [manageParam]);
  const { courses: manageableCourses, refresh: refreshManageableCourses } = useManageableCourses(user?.token);
  const [courseSelectorOpen, setCourseSelectorOpen] = useState(false);
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const socketRef = useRef(getSharedBffWebSocket(apiBaseUrl));
  const pendingRequestIdRef = useRef<string | null>(null);
  const [editingCourseId, setEditingCourseId] = useState<string | null>(null);
  const [hydratingCourse, setHydratingCourse] = useState(false);
  const [clubName, setClubName] = useState('');
  const [courseName, setCourseName] = useState('');
  const [holes, setHoles] = useState<HoleRow[]>(
    Array.from({ length: HOLE_COUNT }, (_, idx) => ({
      number: idx + 1,
      par: '4',
      strokeIndex: String(idx + 1),
    }))
  );
  const [tees, setTees] = useState<TeeRow[]>([
    {
      id: `tee-${Date.now()}`,
      name: 'Yellow',
      sex: 'male',
      courseRating: '72.0',
      slopeRating: '113',
    },
  ]);
  const [teePar, setTeePar] = useState('72');
  // Checkbox maps to the visibility enum: checked = official, unchecked = private. (The DB also
  // supports "pending", which is set elsewhere for now.)
  const [isOfficial, setIsOfficial] = useState(true);
  // Lets the user save even when the live blocking-issue checks fail (e.g. par sum mismatch or a
  // duplicate stroke index from an imperfect scan).
  const [allowSaveWithErrors, setAllowSaveWithErrors] = useState(false);
  const [genderPickerTeeId, setGenderPickerTeeId] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>({ status: 'idle', message: null });
  const [scanState, setScanState] = useState<ScanState>({ status: 'idle', message: null });
  // Seconds elapsed while the AI scan is running, so the button can show live progress
  // (the Claude vision call with adaptive thinking can take up to a minute).
  const [scanElapsed, setScanElapsed] = useState(0);
  const [cameraOpen, setCameraOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Tick a live elapsed-seconds counter while a scan is in flight; reset when it ends.
  useEffect(() => {
    if (scanState.status !== 'scanning') {
      setScanElapsed(0);
      return;
    }
    setScanElapsed(0);
    const startedAt = Date.now();
    const timer = setInterval(() => {
      setScanElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [scanState.status]);

  useEffect(() => {
    const socket = socketRef.current;
    socket.setAuthToken(user?.token ?? null);
    if (isFocused) {
      socket.setSubscriptionContext({ roundId: null, tourId: null });
    }
    const unsubscribe = socket.onMessage((message) => {
      const courseChanged = message as GolfCourseChangedEvent;
      if (courseChanged.type === 'golfCourse.changed') {
        const requestId = courseChanged.requestId ?? courseChanged.RequestId;
        if (!requestId || pendingRequestIdRef.current !== requestId) {
          return;
        }
        const changedCourseId = normalizeGuid(courseChanged.courseId ?? courseChanged.CourseId ?? null);
        if (changedCourseId) {
          setEditingCourseId(changedCourseId);
        }
        pendingRequestIdRef.current = null;
        setSaveState({
          status: 'success',
          message: t('courseConfig.saveSuccess', { id: changedCourseId ?? courseChanged.courseId ?? courseChanged.CourseId ?? '' }),
        });
        // Keep the manage selector in sync so a newly created course appears in the list.
        if (manageMode) {
          refreshManageableCourses();
        }
        if (closeOnSave) {
          setTimeout(() => {
            router.back();
          }, 150);
        }
        return;
      }
      const rejected = message as GolfCourseUpsertRejectedEvent;
      if (rejected.type === 'golfCourse.upsert.rejected') {
        if (!rejected.requestId || pendingRequestIdRef.current !== rejected.requestId) {
          return;
        }
        pendingRequestIdRef.current = null;
        setSaveState({ status: 'error', message: t('courseConfig.saveRejected') });
      }
    });
    return () => unsubscribe();
  }, [closeOnSave, isFocused, manageMode, refreshManageableCourses, router, t, user?.token]);

  // Applies a fetched course detail onto the form state. Shared by the round-hydration effect and the
  // manage-mode course selector.
  const applyCourseDetail = useCallback((course: GeoCourseDetail) => {
    const nextCourseName = (course.name ?? course.Name ?? '').trim();
    const nextAddress = (course.address ?? course.Address ?? '').trim();
    if (nextCourseName) {
      setCourseName(nextCourseName);
    }
    if (nextAddress) {
      setClubName(nextAddress);
    }

    const nextVisibility = (course.visibility ?? course.Visibility)?.toLowerCase();
    if (nextVisibility === 'official' || nextVisibility === 'private' || nextVisibility === 'pending') {
      setIsOfficial(nextVisibility === 'official');
    }

    const incomingHoles = (course.holes ?? course.Holes ?? [])
      .map((hole) => {
        const number = hole.number ?? hole.Number ?? 0;
        const par = hole.par ?? hole.Par ?? 4;
        const strokeIndex = hole.strokeIndex ?? hole.StrokeIndex ?? number;
        return {
          number,
          par: String(par),
          strokeIndex: String(strokeIndex),
        };
      })
      .filter((hole) => hole.number > 0)
      .sort((a, b) => a.number - b.number);
    if (incomingHoles.length > 0) {
      setHoles(incomingHoles);
    }

    const incomingTees = (course.tees ?? course.Tees ?? [])
      .map((tee) => {
        const name = (tee.name ?? tee.Name ?? '').trim();
        const sex = (tee.sex ?? tee.Sex ?? '').trim().toLowerCase();
        const courseRating = tee.courseRating ?? tee.CourseRating;
        const slopeRating = tee.slopeRating ?? tee.SlopeRating;
        const par = tee.par ?? tee.Par;
        return {
          id: normalizeGuid(tee.id ?? tee.Id ?? null) ?? `tee-${Date.now()}-${Math.random().toString(16).slice(2)}`,
          name,
          sex: sex === 'female' || sex === 'unisex' ? sex : 'male',
          courseRating: Number.isFinite(courseRating) ? String(courseRating) : '',
          slopeRating: Number.isFinite(slopeRating) ? String(slopeRating) : '',
          teePar: Number.isFinite(par) ? String(par) : '',
        };
      })
      .filter((tee) => tee.name.length > 0);
    if (incomingTees.length > 0) {
      setTees(
        incomingTees.map((tee) => ({
          id: tee.id,
          name: tee.name,
          sex: tee.sex,
          courseRating: tee.courseRating,
          slopeRating: tee.slopeRating,
        }))
      );
      const firstValidPar = incomingTees.find((tee) => tee.teePar.length > 0)?.teePar;
      if (firstValidPar) {
        setTeePar(firstValidPar);
      }
    }
  }, []);

  // Fetches a course by id and hydrates the form. Used when a course is picked in manage mode.
  const hydrateCourseById = useCallback(async (courseId: string) => {
    if (!user?.token) {
      return;
    }
    setHydratingCourse(true);
    try {
      const courseResponse = await fetch(`${apiBaseUrl}/api/geodata/courses/${courseId}`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      if (!courseResponse.ok) {
        return;
      }
      const course = (await courseResponse.json()) as GeoCourseDetail;
      applyCourseDetail(course);
    } finally {
      setHydratingCourse(false);
    }
  }, [apiBaseUrl, user?.token, applyCourseDetail]);

  // Manage mode: pick an existing course to edit.
  const handleSelectManagedCourse = useCallback((course: ManageableCourse) => {
    setCourseSelectorOpen(false);
    setEditingCourseId(course.id);
    void hydrateCourseById(course.id);
  }, [hydrateCourseById]);

  // Manage mode: reset the form to create a brand-new course.
  const handleCreateNewCourse = useCallback(() => {
    setCourseSelectorOpen(false);
    setEditingCourseId(null);
    setClubName('');
    setCourseName('');
    setIsOfficial(true);
    setTeePar('72');
    setHoles(Array.from({ length: HOLE_COUNT }, (_, idx) => ({ number: idx + 1, par: '4', strokeIndex: String(idx + 1) })));
    setTees([{ id: `tee-${Date.now()}`, name: 'Yellow', sex: 'male', courseRating: '72.0', slopeRating: '113' }]);
    setSaveState({ status: 'idle', message: null });
  }, []);

  useEffect(() => {
    if (!effectiveRoundId || !user?.token || !isNavigationReady) {
      return;
    }

    let cancelled = false;
    const loadRoundCourse = async () => {
      setHydratingCourse(true);
      try {
        const roundResponse = await fetch(`${apiBaseUrl}/api/rounds/${effectiveRoundId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!roundResponse.ok) {
          return;
        }

        const round = (await roundResponse.json()) as RoundSummary;
        const roundCourseName = (round.courseName ?? round.CourseName ?? '').trim();
        const roundCourseAddress = (round.courseAddress ?? round.CourseAddress ?? '').trim();
        const roundCourseId = normalizeGuid(round.courseId ?? round.CourseId ?? null);
        if (cancelled) return;

        if (roundCourseName) {
          setCourseName(roundCourseName);
        }
        if (roundCourseAddress) {
          setClubName(roundCourseAddress);
        }
        if (!roundCourseId) {
          return;
        }

        setEditingCourseId(roundCourseId);
        const courseResponse = await fetch(`${apiBaseUrl}/api/geodata/courses/${roundCourseId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!courseResponse.ok) {
          return;
        }

        const course = (await courseResponse.json()) as GeoCourseDetail;
        if (cancelled) return;

        applyCourseDetail(course);
      } finally {
        if (!cancelled) {
          setHydratingCourse(false);
        }
      }
    };

    void loadRoundCourse();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, effectiveRoundId, isNavigationReady, user?.token, applyCourseDetail]);

  // Web camera: when the capture modal opens, attach the device camera to the <video> preview and
  // stop all tracks when it closes (or on error/unmount).
  useEffect(() => {
    if (!cameraOpen || Platform.OS !== 'web') {
      return;
    }
    let cancelled = false;
    let activeStream: MediaStream | null = null;
    const start = async () => {
      try {
        if (!navigator?.mediaDevices?.getUserMedia) {
          throw new Error('camera unavailable');
        }
        activeStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
          audio: false,
        });
        if (cancelled) {
          activeStream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = activeStream;
        if (videoRef.current) {
          videoRef.current.srcObject = activeStream;
          void videoRef.current.play().catch(() => undefined);
        }
      } catch {
        if (!cancelled) {
          setCameraOpen(false);
          setScanState({ status: 'error', message: t('courseConfig.cameraUnavailable') });
        }
      }
    };
    void start();
    return () => {
      cancelled = true;
      if (activeStream) {
        activeStream.getTracks().forEach((track) => track.stop());
      }
      streamRef.current = null;
    };
  }, [cameraOpen, t]);

  if (!isNavigationReady) return null;
  if (!user) return <Redirect href="/" />;

  const updateHole = (index: number, patch: Partial<HoleRow>) => {
    setHoles((prev) => prev.map((hole, idx) => (idx === index ? { ...hole, ...patch } : hole)));
  };

  const addTee = () => {
    setTees((prev) => [
      ...prev,
      {
        id: `tee-${Date.now()}-${prev.length + 1}`,
        name: '',
        sex: 'male',
        courseRating: '',
        slopeRating: '',
      },
    ]);
  };

  const removeTee = (id: string) => {
    setTees((prev) => prev.filter((tee) => tee.id !== id));
  };

  const updateTee = (id: string, patch: Partial<TeeRow>) => {
    setTees((prev) => prev.map((tee) => (tee.id === id ? { ...tee, ...patch } : tee)));
  };

  const validateHoles = () => {
    const parsed = holes.map((hole) => ({
      number: hole.number,
      par: Number.parseInt(hole.par, 10),
      strokeIndex: Number.parseInt(hole.strokeIndex, 10),
    }));
    if (parsed.some((hole) => !Number.isFinite(hole.par) || hole.par <= 0)) {
      return { ok: false, message: t('courseConfig.invalidPar') };
    }
    if (parsed.some((hole) => !Number.isFinite(hole.strokeIndex) || hole.strokeIndex <= 0)) {
      return { ok: false, message: t('courseConfig.invalidStrokeIndex') };
    }
    return { ok: true, holes: parsed } as const;
  };

  const validateTees = () => {
    if (tees.length === 0) {
      return { ok: false, message: t('courseConfig.teesRequired') } as const;
    }
    const parsedTeePar = Number.parseInt(teePar, 10);
    if (!Number.isFinite(parsedTeePar) || parsedTeePar <= 0) {
      return { ok: false, message: t('courseConfig.invalidTeePar') } as const;
    }

    const parsed = tees.map((tee) => ({
      name: tee.name.trim(),
      sex: tee.sex.trim().toLowerCase(),
      teeColor: tee.name.trim().toLowerCase(),
      courseRating: Number.parseFloat(tee.courseRating),
      slopeRating: Number.parseInt(tee.slopeRating, 10),
      par: parsedTeePar,
    }));

    if (parsed.some((tee) => !tee.name)) {
      return { ok: false, message: t('courseConfig.teeNameRequired') } as const;
    }
    if (parsed.some((tee) => tee.sex !== 'male' && tee.sex !== 'female' && tee.sex !== 'unisex')) {
      return { ok: false, message: t('courseConfig.teeGenderRequired') } as const;
    }
    if (parsed.some((tee) => !Number.isFinite(tee.courseRating) || tee.courseRating <= 0)) {
      return { ok: false, message: t('courseConfig.invalidCourseRating') } as const;
    }
    if (parsed.some((tee) => !Number.isFinite(tee.slopeRating) || tee.slopeRating <= 0)) {
      return { ok: false, message: t('courseConfig.invalidSlopeRating') } as const;
    }
    return { ok: true, tees: parsed } as const;
  };

  // Issues that must be fixed before the course can be saved. Surfaced live
  // above the action buttons so the user can correct them as they type.
  const getBlockingIssues = (): string[] => {
    const issues: string[] = [];

    // The sum of every hole's par must match the configured Tee par.
    const parValues = holes.map((hole) => Number.parseInt(hole.par, 10));
    const parsedTeePar = Number.parseInt(teePar, 10);
    if (
      parValues.every((par) => Number.isFinite(par) && par > 0) &&
      Number.isFinite(parsedTeePar) &&
      parsedTeePar > 0
    ) {
      const parSum = parValues.reduce((sum, par) => sum + par, 0);
      if (parSum !== parsedTeePar) {
        issues.push(t('courseConfig.parSumMismatch', { sum: parSum, teePar: parsedTeePar }));
      }
    }

    // Each stroke index must be a number between 1 and 18, used by only one hole.
    const holesByStrokeIndex = new Map<number, number[]>();
    holes.forEach((hole) => {
      const raw = hole.strokeIndex.trim();
      const strokeIndex = Number.parseInt(raw, 10);
      if (!Number.isFinite(strokeIndex) || strokeIndex < 1 || strokeIndex > 18) {
        if (raw !== '') {
          issues.push(t('courseConfig.strokeIndexRange', { hole: hole.number, value: raw }));
        }
        return;
      }
      const list = holesByStrokeIndex.get(strokeIndex) ?? [];
      list.push(hole.number);
      holesByStrokeIndex.set(strokeIndex, list);
    });
    holesByStrokeIndex.forEach((holeNumbers, value) => {
      if (holeNumbers.length > 1) {
        issues.push(t('courseConfig.strokeIndexDuplicate', { value, holes: holeNumbers.join(', ') }));
      }
    });

    return issues;
  };

  const blockingIssues = getBlockingIssues();
  const hasBlockingIssues = blockingIssues.length > 0;

  const onSave = () => {
    if (!clubName.trim()) {
      setSaveState({ status: 'error', message: t('courseConfig.clubRequired') });
      return;
    }
    if (hasBlockingIssues && !allowSaveWithErrors) {
      setSaveState({ status: 'error', message: t('courseConfig.fixBeforeSave') });
      return;
    }
    const validation = validateHoles();
    if (!validation.ok) {
      setSaveState({ status: 'error', message: validation.message });
      return;
    }
    const teeValidation = validateTees();
    if (!teeValidation.ok) {
      setSaveState({ status: 'error', message: teeValidation.message });
      return;
    }

    const requestId = createRequestId();
    const payload = {
      type: 'golfCourse.upsert',
      requestId,
      courseId: editingCourseId ?? undefined,
      source: 'user',
      userId: user.id,
      clubName: clubName.trim(),
      courseName: courseName.trim(),
      holes: validation.holes,
      tees: teeValidation.tees,
      visibility: isOfficial ? 'official' : 'private',
    };

    const socket = socketRef.current;
    const sent = socket.send(payload);
    pendingRequestIdRef.current = requestId;
    setSaveState({
      status: 'saving',
      message: sent ? t('courseConfig.saving') : t('courseConfig.waitingConnection'),
    });
  };

  // Auto-fill par + stroke index (and tee par) from a scanned scorecard. We never guess: any hole
  // the scan didn't confidently read is left BLANK so the user fills it in, rather than showing a
  // misleading default value.
  const applyScan = (data: ScanResponse) => {
    const scannedHoles = Array.isArray(data.holes) ? data.holes : [];
    if (scannedHoles.length > 0) {
      setHoles((prev) =>
        prev.map((row) => {
          const scanned = scannedHoles.find((hole) => hole.number === row.number);
          const parRead = scanned && Number.isFinite(scanned.par);
          const indexRead = scanned && scanned.strokeIndex != null && Number.isFinite(scanned.strokeIndex);
          return {
            ...row,
            par: parRead ? String(scanned!.par) : '',
            strokeIndex: indexRead ? String(scanned!.strokeIndex) : '',
          };
        })
      );
    }
    if (typeof data.totalPar === 'number' && data.totalPar > 0) {
      setTeePar(String(data.totalPar));
    }
    const warning = data.warnings && data.warnings.length > 0 ? data.warnings[0] : null;
    setScanState({ status: 'idle', message: warning ?? t('courseConfig.scanSuccess') });
  };

  const sendScan = async (base64: string, mediaType: string) => {
    if (!user?.token) return;
    setScanState({ status: 'scanning', message: t('courseConfig.scanRunning') });
    try {
      const response = await fetch(`${apiBaseUrl}/api/courses/scan-scorecard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.token}` },
        body: JSON.stringify({ imageBase64: base64, mediaType }),
      });
      if (!response.ok) {
        let message = t('courseConfig.scanFailed');
        let detail: unknown = null;
        try {
          detail = await response.json();
          if ((detail as { error?: string })?.error === 'not_configured') {
            message = t('courseConfig.scanNotConfigured');
          }
        } catch {
          // non-JSON error body
        }
        console.warn('[scorecard] scan failed', response.status, detail);
        setScanState({ status: 'error', message });
        return;
      }
      const data = (await response.json()) as ScanResponse;
      applyScan(data);
    } catch (error) {
      console.warn('[scorecard] scan error', error);
      setScanState({ status: 'error', message: t('courseConfig.scanFailed') });
    }
  };

  const closeCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setCameraOpen(false);
  };

  // Grab the current camera frame as a JPEG and send it to be scanned.
  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    const base64 = dataUrl.includes(',') ? dataUrl.slice(dataUrl.indexOf(',') + 1) : dataUrl;
    closeCamera();
    if (base64) {
      void sendScan(base64, 'image/jpeg');
    }
  };

  const handleNativeScan = async () => {
    const permission = await ImagePicker.requestCameraPermissionsAsync();
    if (!permission.granted) {
      setScanState({ status: 'error', message: t('courseConfig.scanPermissionDenied') });
      return;
    }
    const result = await ImagePicker.launchCameraAsync({ base64: true, quality: 0.85, allowsEditing: false });
    if (result.canceled || result.assets.length === 0) {
      return;
    }
    const asset = result.assets[0];
    if (!asset.base64) {
      setScanState({ status: 'error', message: t('courseConfig.scanFailed') });
      return;
    }
    void sendScan(asset.base64, asset.mimeType ?? 'image/jpeg');
  };

  const onScanPress = () => {
    if (scanState.status === 'scanning' || hydratingCourse) return;
    if (Platform.OS === 'web') {
      setCameraOpen(true);
    } else {
      void handleNativeScan();
    }
  };

  const genderOptions = [
    { value: 'male', label: t('courseConfig.genderMale') },
    { value: 'female', label: t('courseConfig.genderFemale') },
    { value: 'unisex', label: t('courseConfig.genderUnisex') },
  ] as const;

  const resolveGenderLabel = (value: string) => {
    return genderOptions.find((option) => option.value === value)?.label ?? t('courseConfig.genderMale');
  };

  return (
    <ScrollView contentContainerStyle={styles.container} keyboardShouldPersistTaps="handled">
      <View style={styles.card}>
        <Text style={styles.title}>{t('courseConfig.title')}</Text>
        {manageMode ? (
          <View style={styles.manageSelectorGroup}>
            <Text style={styles.label}>{t('courseConfig.manageSelectorLabel')}</Text>
            <Pressable style={styles.genderDropdownButton} onPress={() => setCourseSelectorOpen(true)}>
              <Text style={styles.genderDropdownValue}>
                {editingCourseId
                  ? courseName.trim() || t('courseConfig.manageSelectedCourse')
                  : t('courseConfig.manageCreateNew')}
              </Text>
              <Text style={styles.genderDropdownChevron}>▾</Text>
            </Pressable>
          </View>
        ) : null}
        <View style={styles.inputRow}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>{t('courseConfig.clubLabel')}</Text>
            <TextInput
              style={styles.input}
              value={clubName}
              onChangeText={setClubName}
              onBlur={() => {
                // Default the course name to the club name when the user leaves it blank.
                if (clubName.trim() && !courseName.trim()) {
                  setCourseName(clubName.trim());
                }
              }}
              placeholder={t('courseConfig.clubPlaceholder')}
              placeholderTextColor="rgba(226,232,240,0.6)"
            />
          </View>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>{t('courseConfig.courseLabel')}</Text>
            <TextInput
              style={styles.input}
              value={courseName}
              onChangeText={setCourseName}
              placeholder={t('courseConfig.coursePlaceholder')}
              placeholderTextColor="rgba(226,232,240,0.6)"
            />
          </View>
        </View>
        <View style={styles.inputRow}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>{t('courseConfig.teeParLabel')}</Text>
            <TextInput style={styles.input} value={teePar} onChangeText={setTeePar} keyboardType="number-pad" />
          </View>
        </View>
        <Pressable
          style={styles.officialRow}
          onPress={() => setIsOfficial((prev) => !prev)}
          accessibilityRole="checkbox"
          accessibilityState={{ checked: isOfficial }}
        >
          <View style={[styles.checkbox, isOfficial && styles.checkboxChecked]}>
            {isOfficial ? <Text style={styles.checkboxMark}>✓</Text> : null}
          </View>
          <View style={styles.officialTextGroup}>
            <Text style={styles.officialLabel}>{t('courseConfig.makeOfficialLabel')}</Text>
            <Text style={styles.officialHint}>{t('courseConfig.makeOfficialHint')}</Text>
          </View>
        </Pressable>
        <View style={styles.teeHeaderRow}>
          <Text style={styles.teeSectionTitle}>{t('courseConfig.teesTitle')}</Text>
          <Pressable style={styles.addTeeButton} onPress={addTee}>
            <Text style={styles.addTeeButtonText}>{t('courseConfig.addTeeButton')}</Text>
          </Pressable>
        </View>
        {tees.map((tee, index) => (
          <View key={tee.id} style={styles.teeCard}>
            <View style={styles.teeCardTopRow}>
              <TextInput
                style={styles.teeTitleInput}
                value={tee.name}
                onChangeText={(value) => updateTee(tee.id, { name: value })}
                placeholder={t('courseConfig.teeLabel', { index: index + 1 })}
                placeholderTextColor="rgba(226,232,240,0.55)"
              />
              <Pressable
                style={[styles.removeTeeButton, tees.length <= 1 && styles.removeTeeButtonDisabled]}
                onPress={() => removeTee(tee.id)}
                disabled={tees.length <= 1}
              >
                <Text style={styles.removeTeeButtonText}>{t('courseConfig.removeTeeButton')}</Text>
              </Pressable>
            </View>
            <View style={styles.teeInputsRow}>
              <View style={[styles.teeInputGroup, styles.teeInputGroupGender]}>
                <Text style={[styles.label, styles.teeFieldLabel]}>{t('courseConfig.teeGenderLabel')}</Text>
                <Pressable style={styles.genderDropdownButton} onPress={() => setGenderPickerTeeId(tee.id)}>
                  <Text style={[styles.genderDropdownValue, styles.teeFieldValue]}>{resolveGenderLabel(tee.sex)}</Text>
                  <Text style={styles.genderDropdownChevron}>v</Text>
                </Pressable>
              </View>
              <View style={[styles.teeInputGroup, styles.teeInputGroupRating]}>
                <Text style={[styles.label, styles.teeFieldLabel]}>{t('courseConfig.courseRatingLabel')}</Text>
                <TextInput
                  style={[styles.input, styles.teeFieldInput]}
                  value={tee.courseRating}
                  onChangeText={(value) => updateTee(tee.id, { courseRating: value })}
                  keyboardType="numeric"
                />
              </View>
              <View style={[styles.teeInputGroup, styles.teeInputGroupSlope]}>
                <Text style={[styles.label, styles.teeFieldLabel]}>{t('courseConfig.slopeRatingLabel')}</Text>
                <TextInput
                  style={[styles.input, styles.teeFieldInput]}
                  value={tee.slopeRating}
                  onChangeText={(value) => updateTee(tee.id, { slopeRating: value })}
                  keyboardType="number-pad"
                />
              </View>
            </View>
          </View>
        ))}
        <View style={styles.scanRow}>
          <Pressable
            style={[styles.scanButton, (scanState.status === 'scanning' || hydratingCourse) && styles.actionButtonDisabled]}
            onPress={onScanPress}
            disabled={scanState.status === 'scanning' || hydratingCourse}
          >
            {scanState.status === 'scanning' ? (
              <ActivityIndicator color={appColors.text.primary} />
            ) : (
              <Ionicons name="camera" size={18} color={appColors.text.primary} />
            )}
            <Text style={styles.scanButtonText}>
              {scanState.status === 'scanning'
                ? `${t('courseConfig.scanRunning')} ${scanElapsed}s`
                : t('courseConfig.scanButton')}
            </Text>
          </Pressable>
          {scanState.status === 'scanning' ? (
            <Text style={styles.statusText}>{t('courseConfig.scanThinkingHint')}</Text>
          ) : scanState.message ? (
            <Text style={scanState.status === 'error' ? styles.errorText : styles.statusText}>{scanState.message}</Text>
          ) : null}
        </View>
        <View style={styles.tableHeader}>
          <Text style={[styles.tableCell, styles.holeCell]}>{t('courseConfig.holeHeader')}</Text>
          <Text style={[styles.tableCell, styles.parCell]}>{t('courseConfig.parHeader')}</Text>
          <Text style={[styles.tableCell, styles.strokeCell]} numberOfLines={1} ellipsizeMode="tail">
            {t('courseConfig.strokeIndexHeader')}
          </Text>
        </View>
        {holes.map((hole, index) => (
          <View key={hole.number} style={styles.tableRow}>
            <Text style={[styles.tableCell, styles.holeCell]}>{hole.number}</Text>
            <TextInput
              style={[styles.tableCell, styles.parCell, styles.tableInput]}
              value={hole.par}
              onChangeText={(value) => updateHole(index, { par: value })}
              keyboardType="number-pad"
            />
            <TextInput
              style={[styles.tableCell, styles.strokeCell, styles.tableInput]}
              value={hole.strokeIndex}
              onChangeText={(value) => updateHole(index, { strokeIndex: value })}
              keyboardType="number-pad"
            />
          </View>
        ))}
        {hasBlockingIssues ? (
          <View style={styles.issuesBox}>
            <Text style={styles.issuesTitle}>{t('courseConfig.fixBeforeSave')}</Text>
            {blockingIssues.map((issue, index) => (
              <Text key={index} style={styles.issueText}>
                {'• '}
                {issue}
              </Text>
            ))}
          </View>
        ) : null}
        {hasBlockingIssues ? (
          <Pressable
            style={styles.officialRow}
            onPress={() => setAllowSaveWithErrors((prev) => !prev)}
            accessibilityRole="checkbox"
            accessibilityState={{ checked: allowSaveWithErrors }}
          >
            <View style={[styles.checkbox, allowSaveWithErrors && styles.checkboxChecked]}>
              {allowSaveWithErrors ? <Text style={styles.checkboxMark}>✓</Text> : null}
            </View>
            <Text style={styles.officialLabel}>{t('courseConfig.saveWithErrorsLabel')}</Text>
          </Pressable>
        ) : null}
        <View style={styles.actionRow}>
          <Pressable
            style={[styles.cancelButton, saveState.status === 'saving' && styles.actionButtonDisabled]}
            onPress={() => router.back()}
            disabled={saveState.status === 'saving'}
          >
            <Text style={styles.cancelButtonText}>{t('courseConfig.cancelButton')}</Text>
          </Pressable>
          <Pressable
            style={[styles.saveButton, (saveState.status === 'saving' || hydratingCourse || !clubName.trim() || (hasBlockingIssues && !allowSaveWithErrors)) && styles.actionButtonDisabled]}
            onPress={onSave}
            disabled={saveState.status === 'saving' || hydratingCourse || !clubName.trim() || (hasBlockingIssues && !allowSaveWithErrors)}
          >
            {saveState.status === 'saving' || hydratingCourse ? <ActivityIndicator color={appColors.text.primary} /> : null}
            <Text style={styles.saveButtonText}>{t('courseConfig.saveButton')}</Text>
          </Pressable>
        </View>
        {saveState.message ? (
          <Text style={saveState.status === 'error' ? styles.errorText : styles.statusText}>{saveState.message}</Text>
        ) : null}
      </View>
      <Modal visible={courseSelectorOpen} transparent animationType="fade" onRequestClose={() => setCourseSelectorOpen(false)}>
        <Pressable style={styles.pickerOverlay} onPress={() => setCourseSelectorOpen(false)}>
          <View style={styles.pickerCard}>
            <ScrollView style={styles.manageCourseList} keyboardShouldPersistTaps="handled">
              <Pressable style={styles.pickerOption} onPress={handleCreateNewCourse}>
                <Text style={styles.pickerOptionText}>＋ {t('courseConfig.manageCreateNew')}</Text>
              </Pressable>
              {manageableCourses.map((course) => (
                <Pressable key={course.id} style={styles.pickerOption} onPress={() => handleSelectManagedCourse(course)}>
                  <Text style={styles.pickerOptionText}>
                    {course.name}
                    {course.clubName ? ` · ${course.clubName}` : ''}
                  </Text>
                  {course.visibility && course.visibility !== 'official' ? (
                    <Text style={styles.manageCourseMeta}>{course.visibility}</Text>
                  ) : null}
                </Pressable>
              ))}
              {manageableCourses.length === 0 ? (
                <View style={styles.pickerOption}>
                  <Text style={styles.pickerOptionText}>{t('courseConfig.manageNoCourses')}</Text>
                </View>
              ) : null}
            </ScrollView>
          </View>
        </Pressable>
      </Modal>
      <Modal visible={genderPickerTeeId !== null} transparent animationType="fade" onRequestClose={() => setGenderPickerTeeId(null)}>
        <Pressable style={styles.pickerOverlay} onPress={() => setGenderPickerTeeId(null)}>
          <View style={styles.pickerCard}>
            {genderOptions.map((option) => (
              <Pressable
                key={option.value}
                style={styles.pickerOption}
                onPress={() => {
                  if (genderPickerTeeId) {
                    updateTee(genderPickerTeeId, { sex: option.value });
                  }
                  setGenderPickerTeeId(null);
                }}
              >
                <Text style={styles.pickerOptionText}>{option.label}</Text>
              </Pressable>
            ))}
          </View>
        </Pressable>
      </Modal>
      {Platform.OS === 'web' ? (
        <Modal visible={cameraOpen} transparent animationType="fade" onRequestClose={closeCamera}>
          <View style={styles.cameraOverlay}>
            <View style={styles.cameraCard}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{ width: '100%', maxHeight: 360, borderRadius: 12, backgroundColor: '#000' }}
              />
              <View style={styles.cameraActions}>
                <Pressable style={styles.cameraCancelButton} onPress={closeCamera}>
                  <Text style={styles.cancelButtonText}>{t('courseConfig.cameraCancel')}</Text>
                </Pressable>
                <Pressable style={styles.cameraCaptureButton} onPress={capturePhoto}>
                  <Ionicons name="camera" size={18} color={appColors.text.inverse} />
                  <Text style={styles.saveButtonText}>{t('courseConfig.cameraCapture')}</Text>
                </Pressable>
              </View>
            </View>
          </View>
        </Modal>
      ) : null}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    padding: 20,
    gap: 12,
    backgroundColor: '#011806',
    alignItems: 'center',
  },
  card: {
    width: '100%',
    maxWidth: 420,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
    padding: 16,
    gap: 12,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  inputRow: {
    gap: 12,
  },
  inputGroup: {
    gap: 6,
  },
  label: {
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    color: appColors.text.primary,
  },
  input: {
    borderWidth: 1,
    borderColor: '#A1D493',
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 15,
    color: appColors.text.primary,
    backgroundColor: '#07200D',
  },
  tableHeader: {
    flexDirection: 'row',
    paddingVertical: 8,
    columnGap: 12,
    borderBottomWidth: 1,
    borderColor: '#A1D493',
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 1,
    columnGap: 12,
    borderBottomWidth: 1,
    borderColor: 'rgba(51,69,109,0.6)',
  },
  tableCell: {
    fontSize: 14,
    color: '#C8C8B0',
  },
  holeCell: {
    width: 60,
  },
  parCell: {
    width: 80,
  },
  strokeCell: {
    width: 104,
  },
  tableInput: {
    borderWidth: 1,
    borderColor: '#A1D493',
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 8,
    backgroundColor: '#07200D',
    color: appColors.text.primary,
  },
  officialRow: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 12,
    paddingVertical: 4,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#A1D493',
    backgroundColor: '#07200D',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxChecked: {
    backgroundColor: '#A1D493',
  },
  checkboxMark: {
    color: appColors.text.inverse,
    fontSize: 15,
    fontWeight: '900',
    lineHeight: 17,
  },
  officialTextGroup: {
    flex: 1,
    gap: 2,
  },
  officialLabel: {
    fontSize: 14,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  officialHint: {
    fontSize: 12,
    color: appColors.text.secondary,
  },
  scanRow: {
    marginTop: 14,
    gap: 6,
  },
  scanButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    columnGap: 8,
    paddingVertical: 11,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#A1D493',
    backgroundColor: '#0C2510',
  },
  scanButtonText: {
    color: appColors.text.primary,
    fontSize: 15,
    fontWeight: '700',
  },
  cameraOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  cameraCard: {
    width: '100%',
    maxWidth: 480,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
    padding: 12,
    rowGap: 12,
  },
  cameraActions: {
    flexDirection: 'row',
    columnGap: 10,
  },
  cameraCancelButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#0C2510',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cameraCaptureButton: {
    flex: 1.2,
    flexDirection: 'row',
    columnGap: 8,
    paddingVertical: 12,
    borderRadius: 12,
    backgroundColor: '#A1D493',
    alignItems: 'center',
    justifyContent: 'center',
  },
  teeHeaderRow: {
    marginTop: 14,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    columnGap: 12,
  },
  teeSectionTitle: {
    fontSize: 14,
    fontWeight: '700',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: appColors.text.primary,
  },
  addTeeButton: {
    borderWidth: 1,
    borderColor: '#1A3A20',
    borderRadius: 10,
    backgroundColor: '#0C2510',
    paddingHorizontal: 12,
    paddingVertical: 7,
  },
  addTeeButtonText: {
    color: appColors.text.secondary,
    fontSize: 13,
    fontWeight: '700',
  },
  teeCard: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: '#A1D493',
    borderRadius: 12,
    backgroundColor: '#07200D',
    padding: 12,
    rowGap: 10,
  },
  teeCardTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    columnGap: 10,
  },
  teeTitleInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#A1D493',
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#07200D',
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '700',
    marginRight: 8,
  },
  removeTeeButton: {
    borderWidth: 1,
    borderColor: '#7f1d1d',
    borderRadius: 10,
    backgroundColor: '#991b1b',
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  removeTeeButtonDisabled: {
    opacity: 0.45,
  },
  removeTeeButtonText: {
    color: '#fee2e2',
    fontSize: 12,
    fontWeight: '700',
  },
  teeInputsRow: {
    flexDirection: 'row',
    columnGap: 6,
    flexWrap: 'nowrap',
    alignItems: 'flex-end',
  },
  teeInputGroup: {
    flex: 1,
    gap: 6,
    minWidth: 0,
  },
  teeInputGroupGender: {
    flex: 1.2,
  },
  teeInputGroupRating: {
    flex: 1,
  },
  teeInputGroupSlope: {
    flex: 1,
  },
  genderDropdownButton: {
    borderWidth: 1,
    borderColor: '#A1D493',
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#07200D',
  },
  manageSelectorGroup: {
    marginBottom: 16,
    gap: 6,
  },
  manageCourseList: {
    maxHeight: 360,
  },
  manageCourseMeta: {
    color: 'rgba(226,232,240,0.6)',
    fontSize: 11,
    marginTop: 2,
    textTransform: 'capitalize',
  },
  genderDropdownValue: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '600',
  },
  genderDropdownChevron: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '600',
  },
  pickerOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.35)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  pickerCard: {
    width: '100%',
    maxWidth: 320,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
    overflow: 'hidden',
  },
  pickerOption: {
    paddingVertical: 14,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
    borderColor: 'rgba(51,69,109,0.6)',
  },
  pickerOptionText: {
    color: appColors.text.primary,
    fontSize: 15,
  },
  teeFieldLabel: {
    fontSize: 9,
  },
  teeFieldInput: {
    fontSize: 12,
  },
  teeFieldValue: {
    fontSize: 11,
  },
  actionRow: {
    marginTop: 12,
    flexDirection: 'row',
    columnGap: 10,
  },
  actionButtonDisabled: {
    opacity: 0.5,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#0C2510',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  cancelButtonText: {
    fontSize: 15,
    fontWeight: '700',
    color: appColors.text.secondary,
  },
  saveButton: {
    flex: 1.2,
    paddingVertical: 12,
    borderRadius: 12,
    backgroundColor: '#A1D493',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  saveButtonText: {
    fontSize: 15,
    fontWeight: '700',
    color: appColors.text.inverse,
  },
  statusText: {
    color: appColors.text.primary,
  },
  errorText: {
    color: '#b91c1c',
  },
  issuesBox: {
    marginTop: 12,
    borderWidth: 1,
    borderColor: '#7f1d1d',
    borderRadius: 12,
    backgroundColor: 'rgba(127,29,29,0.18)',
    padding: 12,
    rowGap: 6,
  },
  issuesTitle: {
    color: '#fca5a5',
    fontSize: 13,
    fontWeight: '700',
  },
  issueText: {
    color: '#fecaca',
    fontSize: 13,
    lineHeight: 18,
  },
});
